import { ComponentType } from '@angular/cdk/overlay'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { AppointmentAuthorizationService, AppointmentDataService, AppointmentModel, AUTHORIZATION_STATUS, AuthorizationModel, ClinicService, INSURANCE_PROVIDER_SOURCE, INSURANCE_PROVIDER_TRANSACTION_TYPE, InsuranceProviderLookup, InsuranceProviderLookupsService, Patient, PATIENT_DATA_PROGRESS_KEY, PatientDataService, PatientInsurance, PatientUploadedFile, Permission, waitUntil } from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { concatMap, filter, finalize, first, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators'

import { AuthorizationDialogComponent, AuthorizationDialogData } from '../authorization-dialog'
import { PatientAuthHistoryDialogComponent } from '../patient-auth-history-dialog'
import { PatientFileViewComponent } from '../patient-file-view'
import { ProgressService } from '../progress'
import { ServiceReviewInquiryDialogComponent, ServiceReviewInquiryDialogData } from '../service-review-inquiry'
import { ServiceReviewSubmissionDialogComponent, ServiceReviewSubmissionDialogData, ServiceReviewSubmissionPreviewComponent } from '../service-review-submission'

@Component({
  selector: 'mmx-authorization-card',
  templateUrl: './authorization-card.component.html',
  standalone: false,
})
export class AuthorizationCardComponent implements OnInit, OnDestroy {
  private readonly onDestroy$ = new Subject<void>()

  public readonly displayedColumns: string[] = [
    'appointment',
    'number',
    'insurance',
    'effectiveDate',
    'authorizationStatusText',
    'quantity',
    'attachedTo',
    'note',
    'updatedBy',
    'edit',
  ]

  public readonly Permission = Permission
  public readonly AUTHORIZATION_STATUS = AUTHORIZATION_STATUS

  public canPullPriorAuthorization$ = this.patientDataService.isPriorAuthorizationPullAvailable$

  public tz: string
  public saving$ = new BehaviorSubject<boolean>(false)

  public loading$ = combineLatest([
    this.patientDataService.progressService.inProgress(PATIENT_DATA_PROGRESS_KEY.AUTHORIZATIONS),
    this.saving$,
  ])
    .pipe(
      map(([progress, saving]) => progress || saving),
    )

  public patient: Patient
  public appointment: AppointmentModel
  public authorizations$ = this.patientDataService.authorizations$.pipe(takeUntil(this.onDestroy$))

  constructor(
    private readonly dialog: MatDialog,
    public readonly clinicService: ClinicService,
    public readonly data: AppointmentDataService,
    public readonly patientDataService: PatientDataService,
    private readonly authorizationSvc: AppointmentAuthorizationService,
    private readonly translate: TranslateService,
    private readonly progressService: ProgressService,
    private readonly insuranceProviderLookupsService: InsuranceProviderLookupsService,
  ) {
    this.tz = this.clinicService.clinic.settings.defaultTimezone ?? 'America/New_York'
  }

  ngOnInit(): void {
    this.data.appointment$.pipe(takeUntil(this.onDestroy$)).subscribe(appointment => {
      this.appointment = appointment
    })

    this.patientDataService.patient$.pipe(takeUntil(this.onDestroy$)).subscribe(patient => {
      this.patient = patient
    })

    this.data.appointmentUpdated$
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        this.patientDataService.refreshAuthorizations$.next()
      })
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public fetchAppointments$ = new Subject<void>()
  private attachedAppointmentObservables = new Map<AuthorizationModel, Observable<AppointmentModel[]>>()

  getAttachedAppointments(authorization: AuthorizationModel): Observable<AppointmentModel[]> {
    if (this.attachedAppointmentObservables.has(authorization)) {
      return this.attachedAppointmentObservables.get(authorization)
    }

    const obs = this.patientDataService.appointments$.pipe(
      takeUntil(this.onDestroy$),
      waitUntil(this.fetchAppointments$),
      map(appointments => appointments.filter(appointment => authorization.appointmentIds.has(appointment.id))),
      shareReplay(1),
    )

    this.attachedAppointmentObservables.set(authorization, obs)

    return obs
  }

  /**
   * Open dialog for add authorization
   */
  openAuthorizationDialog(authorization?: AuthorizationModel): void {
    const data: AuthorizationDialogData = {
      appointment: this.appointment,
      authorization,
      timezone: this.tz,
    }

    this.dialog.open(AuthorizationDialogComponent, {
      width: '800px',
      data,
      panelClass: 'dialog-form',
      autoFocus: false,
    })
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
        tap(() => this.refresh()),
      )
      .subscribe()
  }

  openPullServiceReviewsDialog(patient: Patient): void {
    const data: ServiceReviewInquiryDialogData = {
      patient,
      type: 'authorizations',
      appointment: this.appointment,
    }

    this.dialog.open(ServiceReviewInquiryDialogComponent, {
      width: '800px',
      panelClass: 'dialog-form',
      data,
    })
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
        tap(() => this.refresh()),
      )
      .subscribe()
  }

  viewFile(authorization: AuthorizationModel, file: PatientUploadedFile) {
    const dialogRef = this.dialog.open(PatientFileViewComponent, {
      panelClass: 'dialog-form',
      width: file.extension === '.pdf' ? '2000px' : '500px',
      data: file,
    })

    dialogRef.afterClosed().subscribe((fileDeleted) => {
      if (fileDeleted) {
        const index = authorization.attachments.findIndex((f) => f === file)
        authorization.attachments.splice(index, 1)
      }
    })
  }

  deleteAuthorization(auth: AuthorizationModel): void {
    this.saving$.next(true)
    this.authorizationSvc.deleteAuthorization(auth.id).pipe(
      takeUntil(this.onDestroy$),
      tap(() => {
        this.refresh()
        this.saving$.next(false)
      }),
    ).subscribe()
  }

  checkboxChecked(authorization: AuthorizationModel) {
    return this.appointment?.id
      ? authorization.appointmentIds?.has(this.appointment.id)
      : false
  }

  checkboxChanged(authorization: AuthorizationModel) {
    if (authorization.appointmentIds.has(this.appointment.id)) {
      authorization.appointmentIds.delete(this.appointment.id)
    } else {
      authorization.appointmentIds.add(this.appointment.id)
    }

    this.save(authorization)
  }

  isOutdated(authorization: AuthorizationModel) {
    const auth = this.appointment?.authorizations.find(({ id }) => id === authorization.id)
    return auth?.outdated ?? false
  }

  showHistory(authorization: AuthorizationModel) {
    this.dialog.open(PatientAuthHistoryDialogComponent, {
      data: { authorization },
      panelClass: 'dialog-form',
    })
  }

  private refresh() {
    this.patientDataService.refreshAuthorizations$.next()
    // the appointment's authorizationStatus may have changed, which powers the status icons
    this.data.appointmentUpdated$.next()
  }

  private save(authorization: AuthorizationModel) {
    this.saving$.next(true)
    this.authorizationSvc.updateAuthorization(authorization.id, authorization.toJSON()).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.saving$.next(false)),
      tap(() => {
        this.refresh()
        this.progressService.stop(this.translate.instant('patient-details.authorization-card.auths-saved'))
      }),
    ).subscribe()
  }

  isAuthSubmissionSupported(authorization: AuthorizationModel) {
    return authorization.status === AUTHORIZATION_STATUS.NOT_INITIATED
  }

  private isCarrierSupported(lookup?: InsuranceProviderLookup): boolean {
    return (
      lookup?.provider?.source === INSURANCE_PROVIDER_SOURCE.AVAILITY &&
      lookup?.provider?.supportedTransactions?.includes(INSURANCE_PROVIDER_TRANSACTION_TYPE.AUTH_REF)
    )
  }

  openSubmitToPayerDialog(patient: Patient, authorization?: AuthorizationModel): void {
    this.getInsurance(authorization).pipe(
      takeUntil(this.onDestroy$),
      first(),
      concatMap(patientInsurance => this.insuranceProviderLookupsService.get(patientInsurance.carrierLookupId).pipe(
        map(providerLookup => [patientInsurance, providerLookup] as [PatientInsurance, InsuranceProviderLookup]),
      )),
      switchMap(([patientInsurance, providerLookup]) => {
        const data: ServiceReviewSubmissionDialogData = {
          appointment: this.appointment,
          patient,
          authorization,
          patientInsurance,
          providerLookup,
        }

        const dialogComponent: ComponentType<any> = this.isCarrierSupported(providerLookup) ?
          ServiceReviewSubmissionDialogComponent : ServiceReviewSubmissionPreviewComponent

        return this.dialog.open(dialogComponent, {
          width: '800px',
          data,
          panelClass: 'dialog-form',
        }).afterClosed()
      }),
      tap(() => this.refresh()),
    ).subscribe()
  }

  getInsurance(auth: AuthorizationModel) {
    return this.patientDataService.insurances$.pipe(
      takeUntil(this.onDestroy$),
      map(insurances => insurances?.find(({ id }) => id === auth.insuranceId)),
    )
  }

  openRawResponse(auth: AuthorizationModel) {
    const newWindow = window.open()
    if (newWindow) {
      const html = `<pre><code>${JSON.stringify(
        {
          id: auth.id,
          response: auth.fullAuthorizationObject,
        },
        null,
        2,
      )}</code></pre>`
      newWindow.document.write(html)
    } else {
      alert('A popup blocker prevented us from opening the raw response')
    }
  }
}
